import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { APP_ROUTE } from './app.routes';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { JwtInterceptor } from '@core/interceptor/jwt.interceptor';
import { ErrorInterceptor } from '@core/interceptor/error.interceptor';
import { EnterpriseLockInterceptor } from '@core/interceptor/enterprise-lock.interceptor';
import { LanguageInterceptor } from '@core/interceptor/language.interceptor';
import { DirectionService, LanguageService } from '@core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { FeatherModule } from 'angular-feather';
import { allIcons } from 'angular-feather/icons';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNgxMask } from 'ngx-mask';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginatorIntl } from '@shared/custom-paginator-intl';
import { CustomSnackbarComponent } from '@shared/custom-snackbar.component';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { initializeKeycloak } from '@core/auth/keycloak.config';
import { BrandingService } from '@core/service/branding.service';

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeBranding(brandingService: BrandingService): () => void {
  return () => brandingService.init();
}

// Configuração de formatos de data para MomentDateAdapter
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(APP_ROUTE),
    provideAnimations(),
    DirectionService,
    LanguageService,
    // Keycloak Service
    KeycloakService,
    importProvidersFrom(KeycloakAngularModule),
    // Keycloak Initialization
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'pt',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    provideNgxMask(),
    // Configuração global do MomentDateAdapter
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => {
        const lang = localStorage.getItem('lang') || 'pt';
        const localeMap: { [key: string]: string } = {
          'pt': 'pt-br',
          'en': 'en',
          'es': 'es'
        };
        return localeMap[lang] || 'pt-br';
      }
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS
    },
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const lang = localStorage.getItem('lang') || 'pt';
        const localeMap: { [key: string]: string } = {
          'pt': 'pt-BR',
          'en': 'en-US',
          'es': 'es-ES'
        };
        return localeMap[lang] || 'pt-BR';
      }
    },
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    { provide: CustomSnackbarComponent },
    importProvidersFrom(
      FeatherModule.pick(allIcons)
    ),
    provideCharts(withDefaultRegisterables()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EnterpriseLockInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    provideAnimationsAsync(),
  ],
};
